import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { MatIcon } from "@angular/material/icon";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { filter } from "rxjs";
import { DetectionService } from "../../../services/detection/detection.service";
import { AreaMode } from "../../../model/detection/detection.models";
import { AreaSummaryCardComponent } from "../../components/area-summary-card/area-summary-card.component";
import { SearchGeofencingDialogComponent } from "../../../dialog/search-geofencing/search-geofencing-dialog.component";
import { MapActionsService } from "../../../../shared/services/map-actions.service";
import { MapSelectionType } from "../../../model";
import { MapService } from "../../../../viewer-components/services/internal-services/map.service";
import { GeofencingService } from "../../../../shared/services/geofencing.service";

interface DrawMode {
  id: AreaMode;
  label: string;
  desc: string;
  icon: string;
  mapTool: MapSelectionType;
}

@Component({
  selector: "lib-draw-area-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "flex-fill d-block overflow-hidden" },
  imports: [MatIcon, MatButton, AreaSummaryCardComponent, MatIconButton],
  templateUrl: "./draw-area-panel.component.html",
  styleUrl: "./draw-area-panel.component.scss",
})
export class DrawAreaPanelComponent implements OnInit {
  readonly closeRequested = output<void>();

  protected readonly detection = inject(DetectionService);

  private readonly mapActionsService = inject(MapActionsService);

  private readonly mapService = inject(MapService);

  private readonly dialog = inject(MatDialog);

  private readonly injector = inject(Injector);

  private readonly geofencingService = inject(GeofencingService);

  private readonly destroyRef = inject(DestroyRef);

  /** True once the map selection tools have been initialized and are ready to use. */
  protected readonly mapReady = toSignal(this.mapService.mapInitialized$, { initialValue: false });

  protected readonly drawModes: DrawMode[] = [
    {
      id: "point",
      label: "Point + Radius",
      icon: "circle",
      desc: "Select a point and define detection radius.",
      mapTool: MapSelectionType.CIRCLE,
    },
    {
      id: "rectangle",
      label: "Rectangle",
      icon: "box",
      desc: "Draw a rectangular detection area.",
      mapTool: MapSelectionType.SQUARE,
    },
    {
      id: "polygon",
      label: "Custom Polygon",
      icon: "poly",
      desc: "Draw a custom shaped detection area.",
      mapTool: MapSelectionType.POLYGON,
    },
  ];

  protected readonly selectedMode = signal<AreaMode>(null);

  protected readonly isImportMode = computed(() => {
    const m = this.detection.area().mode;
    return m === "boundary" || m === "locations";
  });

  protected readonly hasArea = computed(
    () => !!this.detection.area().geometryWkt || !!this.detection.area().importedFile,
  );

  /** Active request ID — used to match the result coming back from the map. */
  private pendingRequestId: string | null = null;

  ngOnInit(): void {
    // Subscribe to map drawing results for this component's requests
    this.mapActionsService.geofencingSelectionResult$
      .pipe(
        filter((result) => !!result && result.id === this.pendingRequestId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.pendingRequestId = null;
        if (result!.wkt) {
          const mode = this.selectedMode();
          // Circle (point + radius) selection comes back as a
          // GEOMETRYCOLLECTION(POINT, POLYGON). Downstream consumers (the
          // detect API, the map painter) expect a plain POLYGON — extract it.
          const wkt =
            mode === "point" ? this.extractPolygonFromGeometryCollection(result!.wkt) ?? result!.wkt : result!.wkt;
          this.detection.setArea({
            mode,
            geometryWkt: wkt,
          });
        }
      });
  }

  /**
   * Pulls the first POLYGON(...) substring out of a WKT (typically a
   * GEOMETRYCOLLECTION). Walks parens by depth so polygons with inner rings
   * (holes) are returned intact. Returns null when no POLYGON is present.
   */
  private extractPolygonFromGeometryCollection(wkt: string): string | null {
    const start = wkt.search(/POLYGON\s*\(/i);
    if (start === -1) return null;

    let openIdx = start;
    while (openIdx < wkt.length && wkt[openIdx] !== "(") openIdx += 1;
    if (openIdx >= wkt.length) return null;

    let depth = 0;
    for (let i = openIdx; i < wkt.length; i += 1) {
      if (wkt[i] === "(") depth += 1;
      else if (wkt[i] === ")") {
        depth -= 1;
        if (depth === 0) return wkt.substring(start, i + 1);
      }
    }
    return null;
  }

  protected selectDrawMode(id: AreaMode): void {
    this.selectedMode.set(id);
    // Clear any previous geometry when switching modes
    this.detection.setArea({ mode: id });
    this.activateMapTool(id);
  }

  private activateMapTool(mode: AreaMode): void {
    const drawMode = this.drawModes.find((m) => m.id === mode);
    if (!drawMode) return;

    this.pendingRequestId = `geofencing-custom-elements-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    this.mapActionsService.requestGeofencingSelection({
      id: this.pendingRequestId,
      type: drawMode.mapTool,
      data: { context: "custom-elements", mode },
    });
  }

  private cancelActiveMapTool(): void {
    if (this.pendingRequestId) {
      // Same pattern as map-container's enableLocationSearch() to force-deactivate tools
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.mapActionsService.requestGeofencingSelection({ id: this.pendingRequestId, type: null } as any);
      this.pendingRequestId = null;
    }
  }

  protected openImportBoundaryDialog(): void {
    this.cancelActiveMapTool();
    this.selectedMode.set("boundary");
    this.geofencingService.getAvailableLayers$().subscribe((availableLayers) => {
      SearchGeofencingDialogComponent.open(
        this.dialog,
        {
          title: "Import Detection Area",
          description: "Define a geographical area to filter detection results",
          allowedFileTypes: [".geojson", ".wkt", ".kml", ".kmz", ".zip", ".csv"],
          canUseMapSelection: false,
          availableLayers,
          // Detection areas aren't subject to the embeddings search area limit, and applying
          // one shouldn't warm up the embeddings cache.
          enforceEmbeddingsAreaLimit: false,
        },
        this.injector,
      )
        .afterClosed()
        .subscribe((result) => {
          if (result?.success) {
            this.pendingRequestId = null;
            this.mapActionsService.requestGeofencingSelection({ id: this.pendingRequestId, type: null } as any);
            this.detection.setAreaFromGeofencing(result);
          }
        });
    });
  }

  protected clearArea(): void {
    this.cancelActiveMapTool();
    // Activate and immediately cancel a polygon tool to flush the geofencing
    // selection layer — without this the previously drawn shape stays visible.
    this.activateMapTool("polygon");
    this.cancelActiveMapTool();
    this.selectedMode.set(null);
    this.detection.setArea({ mode: "polygon" });
  }

  protected applyArea(): void {
    this.cancelActiveMapTool();
    this.detection.setStage("configure");
  }
}
